import { autoUpdate, computePosition, flip, limitShift, offset, shift, size } from '@floating-ui/dom';
import sortBy from 'lodash/sortBy';

import { addClass, removeClass } from '../utils/classes';
import { buildPropsHelper } from './helpers/props';

export default {
  LiveSuperSelect: {
    ...buildPropsHelper(),

    mounted() {
      this.isDestroyed = false;
      this.onClickCallback = this.onClick.bind(this);
      this.onOptionsScrollCallback = this.onOptionsScroll.bind(this);
      this.onCloseCallback = this.onClose.bind(this);
      this.onFocusInCallback = this.onFocusIn.bind(this);
      this.onFocusOutCallback = this.onFocusOut.bind(this);
      this.onFocusEventCallback = this.onFocusEvent.bind(this);

      this.initialize();
    },

    updated() {
      if (this.propsChanged()) {
        this.unregisterListeners();
        this.initialize();
      }

      this.updateDropdownContentPosition();

      this.handleHighlightOfSelectedOption();
    },

    getProps() {
      return {
        eventTarget: this.el.dataset.eventTarget,
        onBlurEvent: this.el.dataset.onBlurEvent,
        onCloseEvent: `phx:${this.el.dataset.onCloseEvent}`,
        hiddenInputName: this.el.dataset.hiddenInputName,
        filterOnSearch: this.el.dataset.filterOnSearch === 'true',
        optionsShown: this.el.dataset.showOptions === 'true',
      };
    },

    initialize() {
      this.setProps();

      this.optionsListElement = this.el.querySelector('[data-options-list]');
      this.optionsSearchElement = this.el.querySelector('[data-options-search]');

      this.contentElement = this.el.querySelector('[data-dropdown]');

      this.autoUpdateCleanup = autoUpdate(this.el, this.contentElement, () => {
        this.updateDropdownContentPosition();
      });

      this.registerListeners();
      this.handleHighlightOfSelectedOption();
    },

    updateDropdownContentPosition() {
      const rootElement = this.el;
      const contentElement = this.contentElement;

      if (!contentElement) return;

      let options = {
        placement: 'bottom-start',
        strategy: 'fixed',
        middleware: [offset(2), flip(), shift({ padding: 0, limiter: limitShift() })],
      };

      computePosition(rootElement, contentElement, options).then(({ x, y }) => {
        if (this.isDestroyed || !rootElement.isConnected || !contentElement.isConnected) return;

        Object.assign(contentElement.style, {
          left: `${x}px`,
          top: `${y}px`,
          minWidth: `${rootElement.offsetWidth}px`,
        });
      });
    },

    destroyed() {
      this.isDestroyed = true;
      this.unregisterListeners();
    },

    registerListeners() {
      window.addEventListener(this.onCloseEvent, this.onCloseCallback);
      window.addEventListener('phx:focusInput', this.onFocusEventCallback);

      this.el.addEventListener('focus', this.onFocusInCallback);
      this.el.addEventListener('focusout', this.onFocusOutCallback);

      if (this.optionsShown) {
        document.addEventListener('click', this.onClickCallback);
      }

      if (this.optionsListElement && this.optionsSearchElement) {
        this.optionsListElement.addEventListener('scroll', this.onOptionsScrollCallback);
      }
    },

    unregisterListeners() {
      if (this.autoUpdateCleanup) {
        this.autoUpdateCleanup();
        this.autoUpdateCleanup = null;
      }

      document.removeEventListener('click', this.onClickCallback);
      window.removeEventListener(this.onCloseEvent, this.onCloseCallback);
      window.removeEventListener('phx:focusInput', this.onFocusEventCallback);

      if (this.optionsListElement) {
        this.optionsListElement.removeEventListener('scroll', this.onOptionsScrollCallback);
      }
    },

    handleHighlightOfSelectedOption() {
      if (!this.optionsShown || this.filterOnSearch) return;

      const firstMatchingOption = sortBy(this.el.querySelectorAll('[data-option-matches="true"]'), e =>
        parseInt(e.dataset.optionIndex, 10),
      )[0];

      if (firstMatchingOption) {
        this.optionsListElement.scrollTop = firstMatchingOption.offsetTop - firstMatchingOption.clientHeight * 2;

        firstMatchingOption.classList.add('bg-gray-100');

        this.onOptionsScrollCallback();
      } else {
        this.optionsListElement.scrollTop = 0;
      }
    },

    onClick(event) {
      const isClickInside = this.el.contains(event.target);

      if (!isClickInside) this.pushEventTo(this.el, 'close', {});
    },

    onFocusIn() {
      this.pushEventTo(this.el, 'open', {});
    },

    onFocusOut(params) {
      const { relatedTarget, target } = params;

      if (this.optionsSearchElement && this.optionsSearchElement.contains(target)) return;
      if (!relatedTarget || !this.el.contains(relatedTarget)) this.pushEventTo(this.el, 'close', {});
    },

    onFocusEvent({ detail: { name } }) {
      if (this.hiddenInputName === name) this.pushEventTo(this.el, 'open', {});
    },

    onClose() {
      const rootElement = this.el;
      const eventTarget = this.eventTarget;
      const hiddenInputName = this.hiddenInputName;
      const onBlurEvent = this.onBlurEvent;

      setTimeout(() => {
        if (this.isDestroyed || !rootElement.isConnected) return;

        if (onBlurEvent)
          if (eventTarget) this.pushEventTo(eventTarget, onBlurEvent, { name: hiddenInputName });
          else this.pushEventTo(rootElement, onBlurEvent, { name: hiddenInputName });
      }, 250); // Delay the input update to the next event loop so input update is triggered and the value is correctly updated before validation
    },

    onOptionsScroll() {
      if (!this.optionsListElement || !this.optionsSearchElement) return;

      const scroll = this.optionsListElement.scrollTop;

      if (scroll < 0) return;

      const maxScrollTop = this.optionsListElement.scrollHeight - this.optionsListElement.clientHeight;

      if (scroll === maxScrollTop) return;
      if (scroll > 5) return addClass(this.optionsSearchElement, 'shadow-md');
      if (scroll <= 10) removeClass(this.optionsSearchElement, 'shadow-md');
    },
  },
};
